import { Alert, Text } from "@mantine/core";
import {
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AlertPresets } from "../../../../mantine/mantinePresets";
import { MeteringDirection } from "../../../../types/api.types";
import { FormFieldController } from "../../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { type SiteSetupProcessForForm } from "../../SiteSetupAssistant.types";
import { validateMeteringOrMarketLocation } from "../CustomFields/MeterTable/validateMeter";
import { SITE_SETUP_ASSISTANT_FORM } from "../SiteSetupAssistantForms.constants";

interface ConnectionFormItemsProps {
  formControl: Control<SiteSetupProcessForForm>;
  formErrors: FieldErrors<SiteSetupProcessForForm>;
  watch: UseFormWatch<SiteSetupProcessForForm>;
  onSetFormValue: UseFormSetValue<SiteSetupProcessForForm>;
}

function ConnectionFormItems({
  formControl,
  formErrors,
  watch
}: ConnectionFormItemsProps) {
  const { t } = useTranslation();

  const connectionMeteringDirection = watch("connectionMeteringDirection");
  const voltageLevelDeterminedAutomatically = watch(
    "voltageLevelDeterminedAutomatically"
  );
  const connectionVoltageLevel = watch("connectionVoltageLevel");
  const gridOperatorDeterminedAutomatically = watch(
    "gridOperatorDeterminedAutomatically"
  );
  const gridOperator = watch("gridOperator");

  return (
    <div className="ConnectionFormItems site-setup-assistant-form">
      {(!voltageLevelDeterminedAutomatically ||
        !gridOperatorDeterminedAutomatically) && (
        <>
          <Text fw={500} mb="sm">
            Netzverknüpfungspunkt
          </Text>
          {!voltageLevelDeterminedAutomatically && (
            <>
              <FormFieldController
                control={formControl}
                data={SITE_SETUP_ASSISTANT_FORM.connectionVoltageLevel}
                error={formErrors.connectionVoltageLevel}
              />
              {!connectionVoltageLevel && (
                <Alert
                  color={AlertPresets.warning.color}
                  icon={AlertPresets.warning.icon}
                  mb="xl"
                >
                  Die Spannungsebene des Netzverknüpfungspunktes konnte nicht
                  aus dem Marktstammdatenregister importiert werden. Bitte
                  ergänzen Sie die Spannungsebene manuell.
                </Alert>
              )}
            </>
          )}
          {!gridOperatorDeterminedAutomatically && (
            <>
              <FormFieldController
                control={formControl}
                data={SITE_SETUP_ASSISTANT_FORM.gridOperator}
                error={formErrors.gridOperator}
              />
              {!gridOperator && (
                <Alert
                  color={AlertPresets.warning.color}
                  icon={AlertPresets.warning.icon}
                  mb="xl"
                >
                  Der Anschlussnetzbetreiber des Netzverknüpfungspunktes konnte
                  nicht aus dem Marktstammdatenregister importiert werden. Bitte
                  ergänzen Sie den Anschlussnetzbetreiber manuell.
                </Alert>
              )}
            </>
          )}
        </>
      )}
      <Text fw={500} mb="sm">
        Netzübergabe
      </Text>
      <FormFieldController
        control={formControl}
        data={SITE_SETUP_ASSISTANT_FORM.connectionMeasurementType}
        error={formErrors.connectionMeasurementType}
      />
      <FormFieldController
        control={formControl}
        data={SITE_SETUP_ASSISTANT_FORM.connectionMeteringDirection}
        error={formErrors.connectionMeteringDirection}
      />
      <FormFieldController
        control={formControl}
        data={SITE_SETUP_ASSISTANT_FORM.connectionMeteringServiceProvider}
        error={formErrors.connectionMeteringServiceProvider}
      />
      {connectionMeteringDirection &&
        connectionMeteringDirection !==
          MeteringDirection.Verbrauch_Entnahme && (
          <FormFieldController
            control={formControl}
            data={SITE_SETUP_ASSISTANT_FORM.connectionLocationFeedin}
            error={formErrors.connectionLocationFeedin}
            rules={{
              validate: (value?: string | null) => {
                if (!value) return true;
                const errorMessage = validateMeteringOrMarketLocation(value, t);
                return errorMessage ?? true;
              }
            }}
          />
        )}
      {connectionMeteringDirection &&
        connectionMeteringDirection !==
          MeteringDirection.Erzeugung_Einspeisung && (
          <FormFieldController
            control={formControl}
            data={SITE_SETUP_ASSISTANT_FORM.connectionLocationFeedout}
            error={formErrors.connectionLocationFeedout}
            rules={{
              validate: (value?: string | null) => {
                if (!value) return true;
                const errorMessage = validateMeteringOrMarketLocation(value, t);
                return errorMessage ?? true;
              }
            }}
          />
        )}
      <Alert
        color={AlertPresets.info.color}
        icon={AlertPresets.info.icon}
        mt="xl"
      >
        Eine Mess- oder Marktlokation ist notwendig, um die Lastgänge beim
        zuständigen Messstellenbetreiber anfragen zu können. Stromzähler, welche
        für offizielle Abrechnungsprozesse genutzt werden, werden zumeist von
        einem Messstellenbetreiber (MSB) betrieben. Diese Stromzähler erhalten
        dann eine Mess- und/oder Marktlokation, damit sie in der
        Marktkommunikation (Versand von Messwerten bspw. an den
        Stromlieferanten) eindeutig identifizierbar sind.
      </Alert>
    </div>
  );
}

export { ConnectionFormItems };
